import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TIER_NAMES } from "../../lib/api";
import { START_TRIAL } from "../plan/trial-gate";
import {
  ARCHIVED_FILTER,
  DEFAULT_FILTER,
  FILTERS,
  SORTS,
  filterCount,
  visibleConversations,
  type Conversation,
  type FilterId,
} from "../../lib/conversations";
import { planQuery } from "../../lib/queries";
import { useSeen } from "../../lib/seen";
import { shortWhen } from "../../lib/format";
import { ForwardIcon, PlusIcon, SearchIcon, SortIcon } from "../ui/icons";
import { BrandBar } from "../ui/brand-bar";
import { Button, SecondaryButton } from "../ui/button";
import { WrongAccount, accountKey, whoami } from "../ui/account";
import { useOpenedDoors } from "../doorman/door-opened";
import { DoorChip } from "./chip";
import { ConversationFace, NobFace, PersonFace } from "./mascot";
import { useShellUi } from "./state";
import { UsageNotice } from "../plan/usage-notice";
import { UsageControl } from "./usage-ring";
import type { ShellWorld } from "./use-conversations";

export const NO_DOOR_KICKER = "Not set up";
export const NO_DOOR_LINE = "Hang your own door. It takes one screen.";
/**
 * The pinned row while the Rules step is still owed (ruling R-1006-2). The door
 * exists and is live from the email step, so the row says what is left — tuning
 * it — and never that there is no door.
 */
export const TUNE_KICKER = "Your door is live";
export const TUNE_LINE = "Set its rules. It takes one screen.";
/** The row's own call to action, when the third step is still owed (§2.3.2 #4). */
export const SET_UP_CHIP = "Set up";
export const OWN_KICKER = "Your desk";
/**
 * WHAT FREE MEANS FOR AN OWNER, SAID AS IT IS (wave 83). "Free holds the door"
 * sat over a desk of knocks Nob had fully heard, beside a composer saying
 * "Your Nob doesn't talk on Free", and the two read as a contradiction: on
 * Free, Nob still hears every knocker — the hearing is metered to the
 * knocker's own plan — and what Free leaves out is the owner talking to Nob.
 */
/**
 * THE EMPTY INBOX POINTS SOMEWHERE (wave 90). While the list holds nothing but
 * the owner's own Nob, two buttons stand under it: knock on somebody's door,
 * or hand out your own. Every account has a door (ruling R-1006-2).
 */
export const EMPTY_KNOCK = "Knock on a door";
export const EMPTY_SHARE = "Share your Nob";
export const UPSELL_TITLE = "Nob hears your knockers on Free";
export const UPSELL_LINE = "Chatting with your Nob yourself takes a plan.";

/**
 * The conversation list — the whole of "what is going on", down one edge.
 *
 * This replaces the console that used to sit on top of Home. The console was a
 * summary of four rooms because the rooms were elsewhere; here the rooms are
 * gone and the thing they summarised is the list itself, permanently on screen.
 * A summary of a list you are looking at is furniture.
 *
 * ORDER, top to bottom, and each one earns its place: the brand (where you
 * are), search and the one way out of the list, the upsell only while it is
 * true, your own doorman pinned above everything because it is the one thread
 * that is always yours, the filters, the list, and you.
 */
export function Sidebar({ world }: { world: ShellWorld }) {
  const ui = useShellUi();
  const { conversations, own, doorLive, hasDoor } = world;
  const search = useRef<HTMLInputElement>(null);
  const { markSeen } = useSeen();

  const list = useMemo(
    () =>
      visibleConversations({
        conversations,
        filter: ui.filter,
        sort: ui.sort,
        search: ui.search,
      }),
    [conversations, ui.filter, ui.sort, ui.search],
  );

  const open = (conv: Conversation) => {
    markSeen(conv.id, conv.at);
    ui.setActive(conv.id);
  };

  /**
   * AN OPEN CONVERSATION STAYS READ. The mark used to be set once, on opening;
   * anything that moved the thread while the person was IN it — their own
   * reply, the door opening in front of them, a poll landing — left it
   * "unread" the moment they stepped out (owner, 2026-09-10). While a
   * conversation is the active one, its mark follows its latest activity.
   */
  const active = conversations.find((c) => c.id === ui.activeId);
  const activeAt = active?.at ?? 0;
  // A row wearing the compose thread's id is read under the server's (wave 82).
  const activeThreadId = active?.threadId ?? null;
  useEffect(() => {
    if (!ui.activeId || !activeAt) return;
    markSeen(ui.activeId, activeAt);
    if (activeThreadId) markSeen(activeThreadId, activeAt);
  }, [ui.activeId, activeAt, activeThreadId, markSeen]);

  /**
   * THE PINNED ROW IS NEWS-AWARE (F-28, waiting-room conversion). A doorless
   * registrant's own thread is where the opened-door shelf lives, and the row
   * used to hijack every press into the door-step wizard — so the one person
   * the shelf was written for could never reach it. With a door they waited at
   * now open, the row opens the thread, where the shelf leads above the setup
   * invitation; with no such news it keeps offering the step they deferred.
   * Read only while the step is owed: any other session opens the thread anyway.
   */
  const opened = useOpenedDoors(world.owesDoorStep);

  return (
    <aside data-pane="sidebar" className="flex h-full min-h-0 w-full flex-col bg-sidebar">
      <BrandBar lead={<NobFace mood={doorLive ? "open" : "watching"} size="sm" alt="" />} />

      <div className="flex items-center gap-2 px-3 pb-2">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Search your conversations</span>
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            ref={search}
            data-shell-search
            type="search"
            spellCheck={false}
            value={ui.search}
            onChange={(event) => ui.setSearch(event.target.value)}
            placeholder="Search"
            className="h-9 w-full rounded-full bg-search pr-3 pl-9 text-sm text-text
                       placeholder:text-fainter focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-focus/70"
          />
        </label>
        {/* The one thing you do here that is not reading, so it is the one
            thing here wearing the door's colour. */}
        <button
          type="button"
          aria-label="Knock on a door"
          title="Knock on a door"
          onClick={() => ui.openSheet("knock")}
          className="nob-press grid size-9 shrink-0 place-items-center rounded-full bg-accent
                     text-accent-fg transition-colors duration-75 hover:bg-accent-soft
                     active:bg-accent-strong focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70"
        >
          <PlusIcon className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* THE CONSOLE'S ONE COPY (ruling R-0921-2 (9)). It sits where the
          plan already speaks — above the pinned own-doorman row, under the
          brand — so an owner meets it before they ask for work rather than
          after they are refused. */}
      <ConsoleUsageNotice onOpen={() => ui.openBilling("plans", "options")} />

      <FreePlanCard onOpen={() => ui.openBilling("plans")} />
      <TrialLink onOpen={() => ui.openBilling("plans", "trial")} />

      {own && (
        <OwnRow
          conv={own}
          doorLive={doorLive}
          hasDoor={hasDoor}
          owed={world.owesDoorStep}
          active={ui.activeId === own.id}
          onOpen={() => {
            // Where a knocker goes looking for a door is where setting one up
            // starts (§2.3.3). The row is the same row; what it does depends on
            // whether the third step is still owed — and, when it is, on
            // whether a door they were waiting at has opened (see `opened`).
            if (world.owesDoorStep && ui.setUpDoor && opened.length === 0) {
              return ui.setUpDoor();
            }
            open(own);
          }}
        />
      )}

      <Filters
        conversations={conversations}
        filter={ui.filter}
        sort={ui.sort}
        onFilter={ui.setFilter}
        onSort={ui.setSort}
      />

      <div className="nob-list-seam min-h-0 flex-1 overflow-y-auto px-3 pt-2 pb-2">
        {list.length === 0 ? (
          <Empty filter={ui.filter} searching={ui.search.trim().length > 0} />
        ) : (
          // `gap` rather than a margin on the row: the rows are cards now, and
          // the space between two cards belongs to the list, not to a card.
          <ul className="flex flex-col gap-1.5">
            {list.map((conv) => (
              <li key={conv.id}>
                <Row
                  conv={conv}
                  doorLive={doorLive}
                  query={ui.search}
                  active={ui.activeId === conv.id}
                  onOpen={() => open(conv)}
                />
              </li>
            ))}
          </ul>
        )}
        {/* THE TWO WAYS OUT OF AN EMPTY INBOX (wave 90). Only while the list
            holds nothing but your own Nob — the first conversation retires
            them, with no dismiss and nothing remembered. A phone hides them
            with the list itself whenever a conversation is on screen. */}
        {conversations.length === 0 && (
          <EmptyInboxActions
            onKnock={() => ui.openSheet("knock")}
            second={hasDoor ? { label: EMPTY_SHARE, onClick: () => ui.openSheet("share") } : null}
          />
        )}
      </div>

      <ProfileFooter world={world} />
    </aside>
  );
}

/**
 * The upsell, and the two conditions on it.
 *
 * It is here only while the tier is Free AND the plan lane is switched on at
 * all — `planQuery` resolves to null behind the flag, and a card selling a
 * thing that cannot be bought is the worst kind of advertisement. It states
 * what Free is rather than what it is not: Nob hears the door's knockers, and
 * it always will; talking to Nob is what a plan adds.
 */
/**
 * The console's copy of the usage notice (ruling R-0921-2 (9)). A wrapper and
 * not the notice itself, because the notice is pure props — it also renders on
 * a sender's knock page, which is outside this application entirely.
 */
function ConsoleUsageNotice({ onOpen }: { onOpen: () => void }) {
  const plan = useQuery(planQuery());
  return <UsageNotice plan={plan.data?.plan ?? null} where="console" onAction={onOpen} />;
}

/**
 * THE TRIAL STAYS ON OFFER UNTIL IT IS USED (ruling R-1006-2). One quiet link,
 * only while `GET /plan` reports an offer: no modal, no badge, no counter and no
 * rule about how often it shows. It opens the plan room, where the trial starts.
 */
function TrialLink({ onOpen }: { onOpen: () => void }) {
  const { data: plan } = useQuery(planQuery());
  if (!plan || plan.trial === null) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-trial-link
      className="nob-press mx-3 mb-2 self-start rounded-md px-3 py-1 text-xs text-muted underline
                 underline-offset-2 hover:text-text focus-visible:outline-none
                 focus-visible:ring-2 focus-visible:ring-focus/70"
    >
      {START_TRIAL}
    </button>
  );
}

function FreePlanCard({ onOpen }: { onOpen: () => void }) {
  const { data: plan } = useQuery(planQuery());
  if (!plan || plan.plan.tier !== "free") return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="nob-press mx-3 mb-2 rounded-md bg-search px-3 py-2 text-left hover:bg-selected
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
    >
      <span className="block text-sm font-semibold">{UPSELL_TITLE}</span>
      <span className="block text-xs text-muted">{UPSELL_LINE}</span>
    </button>
  );
}

/**
 * The owner's own doorman, pinned.
 *
 * Every account has a door from the email step on (ruling R-1006-2), so a
 * knocker who stepped aside from the Rules step owes the tuning and nothing
 * else: the same row, in that state, is the way in to it.
 */
function OwnRow({
  conv,
  doorLive,
  hasDoor,
  owed,
  active,
  onOpen,
}: {
  conv: Conversation;
  doorLive: boolean;
  hasDoor: boolean;
  /** The door step is still owed — the run's answer, not "no settings loaded". */
  owed: boolean;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-own-row
      aria-current={active ? "page" : undefined}
      // Same card anatomy as a list row — one padding, one radius — with the
      // pin's own hairline over it, which is what tells the row you always have
      // apart from the rows that come and go.
      className="nob-own-pin nob-row mx-3 mb-2 flex items-center gap-3 rounded-xl px-2.5 py-2.5
                 text-left focus-visible:outline-none focus-visible:ring-2
                 focus-visible:ring-focus/70"
    >
      <ConversationFace conv={conv} doorLive={doorLive} size="xl" />
      <span className="min-w-0 flex-1">
        <span className="nob-own-kicker">
          {owed ? TUNE_KICKER : !hasDoor ? NO_DOOR_KICKER : OWN_KICKER}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[15px] font-semibold tracking-tight">{conv.name}</span>
          {owed ? (
            <span data-chip="pre">{SET_UP_CHIP}</span>
          ) : (
            hasDoor && <DoorChip conv={conv} doorLive={doorLive} compact />
          )}
        </span>
        <span className="mt-0.5 block truncate text-sm text-muted">
          {owed ? TUNE_LINE : !hasDoor ? NO_DOOR_LINE : conv.preview}
        </span>
      </span>
      {hasDoor && !owed && conv.unread > 0 && (
        <span className="nob-unread" aria-label="unread">
          1
        </span>
      )}
    </button>
  );
}

function Filters({
  conversations,
  filter,
  sort,
  onFilter,
  onSort,
}: {
  conversations: Conversation[];
  filter: FilterId;
  sort: (typeof SORTS)[number]["id"];
  onFilter: (id: FilterId) => void;
  onSort: (id: (typeof SORTS)[number]["id"]) => void;
}) {
  const [menu, setMenu] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const shut = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setMenu(false);
    };
    window.addEventListener("click", shut);
    return () => window.removeEventListener("click", shut);
  }, [menu]);

  // Tight on purpose: five tabs, their counts and the sort button have to fit
  // a phone's width and the desktop column without the last one scrolling
  // off (owner, 2026-09-11).
  return (
    <div className="flex items-center gap-0.5 px-2.5 pb-2">
      <div
        role="tablist"
        aria-label="Filter conversations"
        className="nob-filters flex min-w-0 flex-1 flex-nowrap gap-0.5 overflow-x-auto"
      >
        {FILTERS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={filter === entry.id}
            title={entry.hint}
            onClick={() => onFilter(entry.id)}
            className={`nob-press shrink-0 rounded-full px-2 py-1 text-xs font-medium
                        focus-visible:outline-none focus-visible:ring-2
                        focus-visible:ring-focus/70 ${
                          filter === entry.id
                            ? "bg-selected text-text"
                            : "text-muted hover:bg-hover"
                        }`}
          >
            {entry.label}
            <span className="nob-filter-count">{filterCount(conversations, entry.id)}</span>
          </button>
        ))}
      </div>
      <div ref={box} className="relative">
        <button
          type="button"
          aria-label="Sort"
          aria-expanded={menu}
          onClick={() => setMenu((open) => !open)}
          className="nob-press grid size-8 place-items-center rounded-full text-muted
                     hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70"
        >
          <SortIcon className="h-4 w-4" />
        </button>
        {menu && (
          <div
            role="menu"
            aria-label="Sort conversations"
            className="absolute top-9 right-0 z-20 w-48 rounded-md border border-border bg-bg py-1
                       shadow-lg"
          >
            {/* ARCHIVED IS HERE AND NOT IN THE CHIP ROW (ruling R-0828-1). The
                five chips are a partition of live work with a count each, sized
                to fit one line; Archived is where work goes once the reader is
                done with it. A sixth chip would give a drawer the weight of a
                triage lane and push the five that matter into a scroll. It is a
                radio like the sorts below it because it is the same kind of act
                — how am I looking at this list — and because leaving a chip
                selected underneath while Archived is showing would be two
                answers to one question. */}
            <button
              type="button"
              role="menuitemradio"
              aria-checked={filter === ARCHIVED_FILTER.id}
              title={ARCHIVED_FILTER.hint}
              onClick={() => {
                onFilter(filter === ARCHIVED_FILTER.id ? DEFAULT_FILTER : ARCHIVED_FILTER.id);
                setMenu(false);
              }}
              className={`block w-full px-3 py-1.5 text-left text-sm transition-colors duration-75
                          hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                          focus-visible:ring-focus/70 ${
                            filter === ARCHIVED_FILTER.id ? "font-semibold text-text" : "text-muted"
                          }`}
            >
              {ARCHIVED_FILTER.label}
              <span className="nob-filter-count">
                {filterCount(conversations, ARCHIVED_FILTER.id)}
              </span>
            </button>
            <div className="my-1 border-t border-border-soft" />
            {SORTS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="menuitemradio"
                aria-checked={sort === entry.id}
                onClick={() => {
                  onSort(entry.id);
                  setMenu(false);
                }}
                className={`block w-full px-3 py-1.5 text-left text-sm transition-colors duration-75
                            hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                            focus-visible:ring-focus/70 ${
                              sort === entry.id ? "font-semibold text-text" : "text-muted"
                            }`}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  conv,
  doorLive,
  query,
  active,
  onOpen,
}: {
  conv: Conversation;
  doorLive: boolean;
  query: string;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-current={active ? "page" : undefined}
      // The fill, the hover and the open state are `.nob-row`'s (styles.css):
      // a row is a card on the column rather than text on it, and one rule
      // draws that in all three themes.
      className="nob-row flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
    >
      <ConversationFace conv={conv} doorLive={doorLive} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-nowrap items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight">
            <Highlight text={conv.name} query={query} />
          </span>
          <DoorChip conv={conv} doorLive={doorLive} compact />
          <span className="shrink-0 text-xs tabular-nums text-faint">{when(conv.at)}</span>
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-sm text-muted">
            <Highlight text={conv.preview} query={query} />
          </span>
          {conv.unread > 0 && (
            <span className="nob-unread" aria-label="unread">
              1
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function when(at: number): string {
  return at ? shortWhen(new Date(at).toISOString()) : "";
}

/** The search term, marked where it was found. Text nodes only — never HTML. */
export function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const at = text.toLowerCase().indexOf(needle);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="nob-mark">{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
}

function EmptyInboxActions({
  onKnock,
  second,
}: {
  onKnock: () => void;
  /** Share your Nob, or — with no door yet — hang one; null while neither can be offered. */
  second: { label: string; onClick: () => void } | null;
}) {
  return (
    <div data-empty-actions className="flex flex-col gap-2 px-3 pb-4">
      <Button type="button" className="h-10 w-full" onClick={onKnock}>
        {EMPTY_KNOCK}
      </Button>
      {second && (
        <SecondaryButton type="button" className="h-10 w-full" onClick={second.onClick}>
          {second.label}
        </SecondaryButton>
      )}
    </div>
  );
}

function Empty({ filter, searching }: { filter: string; searching: boolean }) {
  const line = searching
    ? "Nothing here matches that."
    : filter === "archived"
      ? "Nothing put away. Archiving takes a conversation out of your lists without telling anybody."
      : filter === "passed"
        ? "Your doorman brings people here once they clear the door."
        : filter === "outgoing"
          ? "Search for a door to knock on. You wait on their step here."
          : filter === "incoming"
            ? "Everyone who knocks lands here, cleared or not."
            : "Nothing yet. Knock on a door, or wait for someone to make their case.";
  return (
    <div className="flex flex-col items-center gap-2 px-6 pt-10 pb-8 text-center">
      <NobFace mood="bored" size="lg" />
      <p className="nob-empty-line">{line}</p>
    </div>
  );
}

/**
 * Who you are, and the two things that hang off it: everything about your
 * account, and how much of the period is gone.
 *
 * The wrong-account escape lives here (approved Part 3b). Sign-in is sticky and
 * this is the only permanent place an identity is stated, so it is the only
 * place somebody who arrived as the wrong one of their two X accounts would
 * ever notice. It is a link, not a button: it must read as an aside, not as a
 * control anybody could hit on the way to Settings.
 */
function ProfileFooter({ world }: { world: ShellWorld }) {
  const ui = useShellUi();
  const { data: plan } = useQuery(planQuery());
  const me = world.me;
  const tier = plan ? TIER_NAMES[plan.plan.tier] : null;
  const name = me?.displayName || (me ? whoami(me) : "You");
  const identity = me ? whoami(me) : "";
  return (
    <div className="border-t border-border/70 px-2 py-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Open settings"
          onClick={() => ui.openSettings("home")}
          className="nob-press flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5
                     text-left hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70"
        >
          {/* The reader's own face, never the doorman's: this button is the one
              permanent statement of who is signed in, and the mascot on it read
              as Nob's account rather than as theirs. */}
          <PersonFace
            name={name}
            handle={me ? accountKey(me) : ""}
            /* THE READER'S OWN PICTURE, FROM `/me` (ruling R-0915-3). It used
               to be read off their own door plate, because `/me` did not carry
               it and there was exactly one copy of it in the product; `/me`
               carries it now, so the footer costs no request of its own and a
               knocker with no plate to read gets their face too. */
            avatar={me?.avatar ?? null}
            size="sm"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{name}</span>
            <span className="block truncate text-xs text-muted">
              {tier ? `${identity} · ${tier}` : identity}
            </span>
          </span>
          <ForwardIcon className="h-4 w-4 shrink-0 text-muted" />
        </button>
        <UsageControl onOpenBilling={() => ui.openBilling("home")} />
      </div>
      {/* The last line of the panel, and the console's only road to the two
          legal pages (owner, 2026-09-18). */}
      {me && <WrongAccount me={me} legal className="px-1.5 pt-1" />}
    </div>
  );
}
